<template>
    <footer class="container-fluid container-body footer-primary pb-4">
        <div class="container pt-5">
            <section class="row">
                <div class="col-12 col-xl-3">
                    <div class="container-logo pb-4 pt-2 text-center">
                        <a href="/">
                            <HeaderLogo />
                        </a>
                    </div>
                </div>
                <FooterLinks
                    title="Login to Your Account"
                    :items="loginLinks"
                    class="col-12 col-md-4 col-lg-3"
                >
                    <template #item="{ uri, linkText }">
                        <a :href="uri" class="py-1">{{ linkText }}</a>
                    </template>
                </FooterLinks>
                <FooterLinks
                    title="Claims Center"
                    :items="claimsCenterLinks"
                    class="col-12 col-md-4 col-lg-3"
                >
                    <template #item="{ to, linkText }">
                        <router-link :to="to || ''" class="py-1">{{ linkText }}</router-link>
                    </template>
                </FooterLinks>
                <FooterLinks
                    title="Quick Links"
                    :items="quickLinks"
                    class="col-12 col-md-4 col-lg-3"
                >
                    <template #item="{ uri, linkText }">
                        <a :href="uri" class="py-1">{{ linkText }}</a>
                    </template></FooterLinks
                >
            </section>
        </div>
        <div class="container pt-4">
            <div class="row">
                <div class="col-12">
                    <hr class="hr mb-4" />
                </div>
            </div>
            <section class="row">
                <div class="col-12">
                    <ul class="list-group list-group-horizontal-md list-group-footer flex-wrap">
                        <li class="list-group-item border-0">
                            <a href="/" class="link-footer"> Home </a>
                        </li>
                        <li class="list-group-item border-0">
                            <router-link :to="{ name: 'support' }" class="link-footer">
                                Support
                            </router-link>
                        </li>
                        <li class="list-group-item border-0">
                            <router-link :to="{ name: 'legal' }" class="link-footer">
                                Terms of Use 
                            </router-link>
                        </li>
                        <li class="list-group-item border-0">
                            <a :href="privacyurl" class="link-footer">
                                Privacy
                            </a>
                        </li>
                        <li id="ca_notice" class="list-group-item border-0">
                            <a :href="privacyDataRequestUrl" class="link-footer">
                                CA Notice at Collection
                            </a>
                        </li>
                        <li class="list-group-item border-0">
                            <a
                                href="https://www.progressive.com/web-site-accessibility/"
                                class="link-footer text-nowrap"
                                target="_blank"
                            >
                                Accessibility Services
                            </a>
                        </li>
                        <li class="list-group-item border-0">
                            <a
                                href="https://www.progressive.com/resources/"
                                class="link-footer text-nowrap"
                            >
                                Insurance Resources
                            </a>
                        </li>
                    </ul>
                </div>

                <div class="col-xs-12 mt-2 mb-3">
                    <p>
                        <a :href="optOutHref" class="link-footer" target="_blank">
                            Do Not Sell or Share My Personal Information (CA Residents Only)
                        </a>
                    </p>
                    <p>
                        <a href="https://www.progressive.com/" class="link-underline">
                            www.progressive.com
                        </a>
                    </p>
                    <p>Copyright 1995 - <span id="yearNow">2025.</span> Progressive Casualty Insurance Company. All Rights Reserved.</p>
                </div>
            </section>
        </div>
    </footer>
</template>

<script setup lang="ts">
import { isUndefinedOrEmpty } from '@/helpers/helpers';
import HeaderLogo from '../icons/IconHeaderLogo.vue';
import { useCommonStore } from '@/stores/common';
import { storeToRefs } from 'pinia';
import { computed, inject, onMounted, ref, type Ref } from 'vue';
import { type Config } from '@/types/config';
import FooterLinks from '../FooterLinks.vue';

const config = inject('config') as Config;

const envVar: String = config.env;

const policyHref = envVar.indexOf('.') == -1 ?`${envVar}policy.americanstrategic.com/Policy/Menu.aspx`:`https://policy${envVar}.americanstrategic.com/Policy/Menu.aspx`;
const portalHref = envVar.indexOf('.') == -1 ?`${envVar}portal.asipolicy.com`:`https://portal${envVar}.asipolicy.com`;

const { globalFeatures } = storeToRefs(useCommonStore())

const optOutHref = ref('');
const privacyurl = ref('');
const privacyDataRequestUrl = ref('');

onMounted(() => {
    useCommonStore().fetchGlobalFeatures();

    const yearNowElement = document.getElementById("yearNow");
    const year = new Date().getFullYear();
    if (yearNowElement) {
        yearNowElement.innerText = year + '.';
    }
});

optOutHref.value = `${config.optOutUrl}?src=${config.optOutParam}`;
privacyurl.value = `${config.privacyUrl}`;
privacyDataRequestUrl.value = `${config.privacyUrl}/privacy-data-request/`;

const showCOWildfireMitigation: Ref<boolean> = computed(() => {
    return (
        !isUndefinedOrEmpty(globalFeatures) &&
        !!globalFeatures.value.get('UI.ShowCOWildfireLink')
    );
});

const loginLinks: { uri: string; linkText: string }[] = [
    {
        uri: portalHref,
        linkText: 'Flood Policyholder Login'
    },
    {
        uri: policyHref,
        linkText: 'Property Agent Login'
    }
];

const claimsCenterLinks: { to: { name: string }; linkText: string }[] = [
    { to: { name: 'claimscenter' }, linkText: 'Claims Center Overview' },
    { to: { name: 'reportfraud' }, linkText: 'Report Fraud' },
    { to: { name: 'claimsfaq' }, linkText: 'Claims FAQs' },
    { to: { name: 'aboutclaimsprocess' }, linkText: 'About the Claims Process' }
];
    const quickLinks = computed(() => [
        {
            uri: '/claims-center/report-a-claim/',
            linkText: 'Report a Claim'
        },
        {
            uri: '/insurance/flood-insurance/',
            linkText: 'Flood Insurance Information'
        },
        {
            uri: 'https://www.progressive.com/homeowners/discounts/florida-wind-mitigation/',
            linkText: 'Florida Wind Mitigation Discount'
        },
        ...(showCOWildfireMitigation.value ? [{
            uri: 'https://www.progressive.com/answers/colorado-home-insurance/',
            linkText: 'Colorado Wildfire Information'
         }] : []),
        {
            uri: 'https://www.progressive.com/answers/oregon-home-insurance/',
            linkText: 'Oregon Wildfire Information'
        },
        {
            uri: 'https://investors.progressive.com/',
            linkText: 'Investor Relations'
        },
        {
            uri: 'https://www.progressive.com/',
            linkText: 'Progressive.com'
        }
    ]);

</script>
